import {
	Component,
	HostListener,
	OnDestroy,
	OnInit,
	ViewChild,
} from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Menu } from 'primeng/menu';
import { MenuItem } from 'primeng/api';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';

// --- Facades ---
import { AppearanceFacade } from '@facades/appearance.facade';
import { AuthFacade } from '@facades/auth.facade';
import { CartFacade } from '@facades/cart.facade';

// --- Interfaces ---
import type { IStore } from '@interfaces/store';
import { IMenuItem } from '../../interfaces/sidebar';
import { SubSink } from 'subsink';
import { filter, shareReplay, take } from 'rxjs';
import { MENUS_COMMON_LOGGED, MENUS_BY_ROLE, MENUS_VISITOR } from './menus';
import { EUserProfileIds } from '../../interfaces/user';
import { canAccessClientArea } from '@utils/client-area-permissions.util';
import { ParametersFacade } from '../../facades/parameters.facade';
import { FavoritesDialogComponent } from '../dialogs/favorites-dialog/favorites-dialog.component';
import { CartDialogComponent } from '../dialogs/cart-dialog/cart-dialog.component';
import { TenantsFacade } from '../../facades/tenants.facade';
import { IFeatures, IModules } from '../../interfaces/tenants';

@Component({
	selector: 'app-sidebar',
	templateUrl: './sidebar.component.html',
	styleUrls: ['./sidebar.component.scss'],
})
export class SidebarComponent implements OnInit, OnDestroy {
	ref?: DynamicDialogRef;
	private subs = new SubSink();

	@ViewChild('settingsMenu') settingsMenu!: Menu;

	address!: IStore['displayedAddress'];
	corporateName!: IStore['corporateName'];
	/** Permissões do perfil do usuário logado, para os itens que dependem delas. */
	private permissions: string[] = [];

	hasPackingFeature: boolean = this.tenantsFacade.hasFlag(
		'enableProductPacking'
	);
	menuItems: IMenuItem[] = [];
	settingsMenuItems: MenuItem[] = [];
	isLoggingOut = false;
	badge: number = 0;
	cartDialogOpen = false;
	favoritesDialogOpen = false;
	isMobile = false;
	isCollapsed = false;
	profile: EUserProfileIds = 0;

	constructor(
		private _router: Router,
		public _dialogService: DialogService,
		private cartFacade: CartFacade,
		private appearanceFacade: AppearanceFacade,
		private parametersFacade: ParametersFacade,
		private authFacade: AuthFacade,
		private tenantsFacade: TenantsFacade
	) {}

	@HostListener('window:scroll')
	onWindowScroll() {
		if (this.settingsMenu.visible) {
			this.settingsMenu.hide();
		}
	}

	@HostListener('window:resize')
	checkScreenSize() {
		const wasMobile = this.isMobile;
		this.isMobile = window.innerWidth <= 650;

		// Se entrou em mobile, começa FECHADO
		if (this.isMobile && !wasMobile) {
			this.isCollapsed = true;
		}

		// Se saiu do mobile para desktop, pode abrir
		if (!this.isMobile && wasMobile) {
			this.isCollapsed = false;
		}

		if (this.isMobile && !this.isCollapsed) {
			document.body.style.overflow = 'hidden';
		} else {
			document.body.style.overflow = '';
		}
	}

	toggleSidebar() {
		this.isCollapsed = !this.isCollapsed;

		if (this.isMobile) {
			document.body.style.overflow = this.isCollapsed ? 'hidden' : '';
		}
	}

	onMenuItemClick(item: IMenuItem, event: Event) {
		if (item.action) {
			item.action(event as MouseEvent);
		}

		if (this.isMobile && item.route) {
			this.isCollapsed = true;
			document.body.style.overflow = '';
		}
	}

	closeSidebarIfMobile() {
		if (this.isMobile) {
			this.isCollapsed = true;
			document.body.style.overflow = '';
		}
	}

	ngOnInit() {
		this.checkScreenSize();

		// Escuta resize em tempo real
		window.addEventListener('resize', () => this.checkScreenSize());

		this.subs.add(
			this.authFacade.authState$.pipe(take(1)).subscribe(({ user }) => {
				const actions = {
					favorites: () => this.favoritesDialog(),
					openSettingsMenu: (event: MouseEvent) => this.openSettingsMenu(event),
				};

				if (!user) {
					this.menuItems = MENUS_VISITOR(actions);
					return;
				}

				this.profile = user.profileId ?? EUserProfileIds.GUEST;
				this.permissions = user.profilePermissions ?? [];
				let roleMenu = MENUS_BY_ROLE(actions)[this.profile] || [];

				const useLegacy = this.tenantsFacade.hasFeatures(
					'useLegacyPermissions'
				);
				roleMenu = roleMenu.filter(item => {
					if (item.route === '/preferences/client-area') {
						return canAccessClientArea(user);
					}
					if (!useLegacy && user?.profilePermissions) {
						const perms = user.profilePermissions;
						if (item.route === '/client-area/invoices')
							return perms.includes('financial.invoices');
						if (item.route === '/client-area/accounts-receivable')
							return perms.includes('financial.receivables');
					}
					return true;
				});

				this.buildSettingsMenu();

				this.menuItems = [...MENUS_COMMON_LOGGED, ...roleMenu]
					// O "Outros" sai da barra quando nenhum item dentro dele sobrou.
					.filter(
						item => item.label !== 'Outros' || this.hasAnySettingsItem
					)
					.map(item => ({
						...item,
						activeOptions: this.defineActiveOptions(item),
					}));
			})
		);

		this.subs.add(
			this._router.events
				.pipe(filter(event => event instanceof NavigationEnd))
				.subscribe(() => {
					this.menuItems = this.menuItems.map(item => ({
						...item,
						activeOptions: this.defineActiveOptions(item),
					}));
				})
		);
	}

	/**
	 * Monta os itens do "Outros". Precisa rodar antes da barra: é o que decide se a
	 * própria aba aparece.
	 */
	private buildSettingsMenu() {
		// Cada item do "Outros" tem permissão própria. A aba passou a existir para o
		// admin do cliente e para o vendedor por causa dos créditos de IA; sem permissão
		// por item, abri-la exporia Integrações e Solicitações junto.
		//
		// O ApplicationAdmin não passa pelas permissões: vê tudo pelo perfil, como antes.
		this.settingsMenuItems = [
			{
				label: 'Integrações',
				icon: 'fa-solid fa-link',
				command: () => {
					this._router.navigate(['/preferences/integrations']);
					this.closeSidebarIfMobile();
				},
				visible: this.canSeeOthersItem('others.integrations'),
			},
			{
				label: 'IA',
				icon: 'fa-solid fa-robot',
				command: () => {
					this._router.navigate(['/preferences/ai']);
					this.closeSidebarIfMobile();
				},
				// Basta uma das permissões da seção: quem tem só as configurações
				// precisa chegar nela, e quem tem só os créditos também.
				visible: this.hasAnyAiAccess,
			},
			{
				label: 'Suporte e Ajuda',
				icon: 'fa-solid fa-circle-question',
				command: () => {
					this._router.navigate(['/preferences/support']);
					this.closeSidebarIfMobile();
				},
				visible: this.canSeeOthersItem('others.support'),
			},
			{
				label: 'Solicitações',
				icon: 'fa-solid fa-person-circle-question',
				command: () => {
					this._router.navigate(['/access-requests']);
					this.closeSidebarIfMobile();
				},
				visible: this.canSeeOthersItem('others.accessRequests'),
			},
		].filter(item => item.visible !== false);
	}

	/** O ApplicationAdmin vê tudo no "Outros"; os demais, só o que a permissão liberar. */
	private canSeeOthersItem(permission: string): boolean {
		return (
			this.profile === EUserProfileIds.APPLICATION_ADMIN ||
			this.permissions.includes(permission)
		);
	}

	/**
	 * A aba "Outros" só aparece quando há algo dentro dela. Um menu que abre vazio é
	 * pior do que não existir: o usuário clica, não acontece nada, e não há como saber
	 * que falta permissão.
	 */
	private get hasAnySettingsItem(): boolean {
		return this.settingsMenuItems.length > 0;
	}

	/**
	 * A tela de créditos de IA no menu Outros.
	 *
	 * Depende de três coisas: o tenant ter contratado o módulo, e o usuário ser de
	 * perfil administrativo ou ter a permissão no perfil. Esconder aqui é conveniência —
	 * quem barra de fato é o guard da rota e a checagem da API.
	 */
	private get hasAiCreditsAccess(): boolean {
		if (!this.tenantsFacade.hasFeatures('aiOrderExtraction')) return false;

		const admin = [
			EUserProfileIds.APPLICATION_ADMIN,
			EUserProfileIds.CUSTOMER_ADMIN,
			EUserProfileIds.PORTAL_MANAGER,
		].includes(this.profile);

		if (admin) return true;

		return this.permissions.includes('ai.creditsAccess');
	}

	/** Configurações de IA. O ApplicationAdmin entra pelo perfil, como antes da mudança. */
	private get hasAiSettingsAccess(): boolean {
		if (!this.tenantsFacade.hasFeatures('aiOrderExtraction')) return false;

		return (
			this.profile === EUserProfileIds.APPLICATION_ADMIN ||
			this.permissions.includes('ai.settings')
		);
	}

	private get hasAnyAiAccess(): boolean {
		return this.hasAiCreditsAccess || this.hasAiSettingsAccess;
	}

	hoveredItem: MenuItem | null = null;

	isActive(item: IMenuItem): boolean {
		const currentUrl = this._router.url;

		// Itens de rota normais
		if (
			item.route &&
			this._router.isActive(item.route, item.activeOptions?.exact ?? false)
		) {
			return true;
		}

		if (item.label === 'Carrinho' && currentUrl.startsWith('/account/cart')) {
			return true;
		}

		if (
			item.label === 'Favoritos' &&
			currentUrl.startsWith('/account/favorites')
		) {
			return true;
		}

		return false;
	}

	private lockScroll() {
		document.body.style.overflow = 'hidden';
	}

	private unlockScroll() {
		document.body.style.overflow = '';
	}

	onSettingsMenuHide() {
		this.unlockScroll();
	}

	openSettingsMenu(event: MouseEvent) {
		this.settingsMenu.toggle(event);
		this.lockScroll();
	}

	get cartTotalItems() {
		return this.cartFacade.totalItems || 0;
	}

	get stockDecimalPlaces() {
		return (this.parametersFacade.parameters?.stockDecimalPlaces || 0) > 0
			? 1
			: 0;
	}

	private defineActiveOptions(item: IMenuItem) {
		const currentUrl = this._router.url;

		if (item.route === '/') {
			return { exact: true };
		}

		if (
			item.route === '/account' &&
			(currentUrl.startsWith('/account/cart') ||
				currentUrl.startsWith('/account/orders') ||
				currentUrl.startsWith('/account/favorites'))
		) {
			return { exact: true }; // não marca o pai
		}

		if (
			item.route === '/dashboard' &&
			currentUrl.startsWith('/dashboard/orders')
		) {
			return { exact: true };
		}

		return { exact: false };
	}

	get appearance() {
		const { logo, useTheme } = this.appearanceFacade;
		return { logo, useTheme };
	}

	favoritesDialog() {
		this.closeCurrentDialog();
		this.favoritesDialogOpen = true;

		this.ref = this._dialogService.open(FavoritesDialogComponent, {
			header: 'Favoritos',
			modal: true,
			styleClass: 'border-round overflow-hidden',
			width: '540px',
		});

		this.subs.add(
			this.ref.onClose.subscribe(() => {
				this.favoritesDialogOpen = false;
				this.ref = undefined;
			})
		);
	}

	private closeCurrentDialog() {
		if (this.ref) {
			this.ref.close();
			this.ref = undefined;
		}

		this.cartDialogOpen = false;
		this.favoritesDialogOpen = false;
	}

	shouldDisplay(item: IMenuItem): boolean {
		if (item.module && !this.hasModule(item.module)) {
			return false;
		}

		return true;
	}

	hasModule(module: keyof IModules) {
		return this.tenantsFacade.hasModule(module);
	}

	hasFeature(feature: keyof IFeatures) {
		return this.tenantsFacade.hasFeatures(feature);
	}

	ngOnDestroy() {
		this.subs.unsubscribe();
	}
}
